import { useEffect, useState } from 'react';
import { useUIStore } from '@/state/store';
import { getDisplayName, apiFetch } from '@/lib/auth';
import { useRoster } from '@/hooks/useRoster';

export function Dashboard() {
  const showPwa = useUIStore((s) => s.showPwaToast);
  const setView = useUIStore((s) => s.setView);
  const { roster } = useRoster();

  const firstName = (getDisplayName() ?? '').trim().split(' ')[0] || 'willkommen';
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);
  const dateLine = `${now.toLocaleDateString('de-DE', { weekday: 'long' })} · ${now.toLocaleDateString('de-DE', { day: 'numeric', month: 'long' })} · ${now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`;

  // Tageszahlen des Beraters (Selbstorientierung – keine Vergleichswerte)
  const [stats, setStats] = useState<{ count: number; totalMinutes: number; avgRating: number | null } | null>(null);
  useEffect(() => {
    let alive = true;
    apiFetch('/api/calls/my-day-stats').then((r) => (r.ok ? r.json() : null))
      .then((s) => { if (alive && s) setStats(s); }).catch(() => { /* leer lassen */ });
    return () => { alive = false; };
  }, []);
  const fmtDuration = (m: number) => (m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} m` : `${m} m`);

  // Offen gebliebene Nachbereitungen
  const [openWrap, setOpenWrap] = useState(0);
  useEffect(() => {
    let alive = true;
    apiFetch('/api/calls/pending-wrapup').then((r) => (r.ok ? r.json() : []))
      .then((x) => { if (alive && Array.isArray(x)) setOpenWrap(x.filter((c: any) => !c.wrapDone).length); })
      .catch(() => { /* */ });
    return () => { alive = false; };
  }, []);

  // Termine heute aus den eigenen Experten-Links
  const [links, setLinks] = useState<any[]>([]);
  useEffect(() => {
    let alive = true;
    apiFetch('/api/links/mine').then((r) => (r.ok ? r.json() : []))
      .then((x) => { if (alive && Array.isArray(x)) setLinks(x); }).catch(() => { /* */ });
    return () => { alive = false; };
  }, []);
  const isToday = (iso?: string) => { if (!iso) return false; const d = new Date(iso); const t = new Date(); return d.toDateString() === t.toDateString(); };
  const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const termine = links
    .filter((l) => (l.status === 'active' || l.status === 'upcoming') && isToday(l.validFrom))
    .sort((a, b) => +new Date(a.validFrom) - +new Date(b.validFrom));

  // Echtzeit-Puls: Aufkommen + Team-Verfügbarkeit über die eigenen Gruppen
  const memberStatus = new Map<string, string>();
  let waiting = 0;
  for (const g of roster.groups) { waiting += g.waiting ?? 0; for (const m of g.members) memberStatus.set(m.id, m.status); }
  let available = 0; memberStatus.forEach((s) => { if (s === 'available') available++; });
  const total = memberStatus.size;
  const pulseColor = waiting > 0 ? (available > 0 ? '#F59E0B' : '#EF4444') : '#22C55E';

  useEffect(() => { const id = setTimeout(showPwa, 7000); return () => clearTimeout(id); }, [showPwa]);

  return (
    <div className="dashboard view active">
      <div className="greeting">
        <div className="greeting-left">
          <div className="hi-eyebrow">{dateLine}</div>
          <div className="hi-name">Guten Tag, <strong>{firstName}</strong></div>
        </div>
        <div className="greeting-right">
          <div className="day-stats">
            <div className="stat-item"><span className="v">{stats ? stats.count : '–'}</span><span className="l">Gespräche heute</span></div>
            <div className="stat-item"><span className="v">{stats ? fmtDuration(stats.totalMinutes) : '–'}</span><span className="l">Gesamtzeit</span></div>
            <div className="stat-item"><span className="v">{stats && stats.avgRating !== null ? stats.avgRating.toFixed(1) : '–'}</span><span className="l">Ø Bewertung</span></div>
          </div>
        </div>
      </div>

      {/* Jetzt: Echtzeit-Puls (Aufkommen + Team-Verfügbarkeit) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '2px 0 16px', fontSize: 13.5 }}>
        <span title={`${waiting} wartend`} style={{ width: 10, height: 10, borderRadius: '50%', background: pulseColor, boxShadow: `0 0 0 4px ${pulseColor}22`, flexShrink: 0 }} />
        <span><strong>{waiting}</strong> {waiting === 1 ? 'Call' : 'Calls'} wartend</span>
        <span style={{ color: 'var(--muted, #8aa)' }}>·</span>
        <span>Team <strong>{available}</strong>/{total} verfügbar</span>
        {available === 0 && total > 0 && <span style={{ color: '#EF4444', fontSize: 12 }}>· niemand verfügbar</span>}
      </div>

      {/* Was auf dich wartet */}
      <div className="now-cards">
        {openWrap > 0 && (
          <div className="now-card accent-2" onClick={() => setView('wrapup')} style={{ cursor: 'pointer' }}>
            <div className="nc-eyebrow">Offen geblieben</div>
            <div className="nc-title">{openWrap} {openWrap === 1 ? 'Nachbereitung' : 'Nachbereitungen'} offen</div>
            <div className="nc-sub">Dokumente und Notizen warten auf finale Ablage</div>
            <div className="nc-action">Zur Nachbereitung →</div>
          </div>
        )}

        <div className="now-card accent-1">
          <div className="nc-eyebrow">Termine heute</div>
          {termine.length === 0 ? (
            <div className="nc-sub" style={{ marginTop: 4 }}>Keine Termine für heute.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '8px 0' }}>
              {termine.slice(0, 5).map((t) => (
                <div key={t.token} style={{ display: 'flex', gap: 10, fontSize: 13, alignItems: 'baseline' }}>
                  <span style={{ fontWeight: 700, minWidth: 46 }}>{fmtTime(t.validFrom)}</span>
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.expectedName || t.targetLabel || 'Gast'}</span>
                  {t.groupName && <span style={{ color: 'var(--muted, #8aa)', fontSize: 12 }}>{t.groupName}</span>}
                </div>
              ))}
              {termine.length > 5 && <div style={{ fontSize: 11.5, color: 'var(--muted, #8aa)' }}>+{termine.length - 5} weitere</div>}
            </div>
          )}
          <div className="nc-action" onClick={() => setView('links')} style={{ cursor: 'pointer' }}>Experten-Links verwalten →</div>
        </div>
      </div>
    </div>
  );
}
